/**
 * ClubCoupons — Moje kupony (aktywne / wykorzystane / wygasłe).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { clubApi } from './clubApi';

const STATUS = {
  active:    { label: 'Aktywny',     color: '#10b981', icon: '🎟️' },
  used:      { label: 'Wykorzystany', color: '#94a3b8', icon: '✅' },
  expired:   { label: 'Wygasł',       color: '#ef4444', icon: '⏰' },
  cancelled: { label: 'Anulowany',    color: '#ef4444', icon: '🚫' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pl-PL', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

export default function ClubCoupons() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    clubApi.get('/coupons/mine')
      .then((r) => setItems(r.data.coupons || []))
      .catch(() => setItems([]));
  }, []);

  const filtered = items === null ? null : items.filter((c) => filter === 'all' || c.status === filter);

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Skopiowano: ${code}`);
    } catch {
      toast.error('Nie udało się skopiować');
    }
  };

  return (
    <div className="club-shell">
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>🎟️ Moje kupony</div>
        <div style={{ fontSize: 12, color: 'var(--club-text-muted)' }}>Pokaż kod obsłudze przy wizycie</div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto' }}>
        {[
          { id: 'active', label: '🎟️ Aktywne' },
          { id: 'used', label: '✅ Wykorzystane' },
          { id: 'expired', label: '⏰ Wygasłe' },
          { id: 'all', label: 'Wszystkie' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`club-coupons-filter-${f.id}`}
            style={{
              padding: '8px 12px', borderRadius: 8, border: 'none', fontFamily: 'inherit',
              background: filter === f.id ? 'var(--club-gold)' : 'rgba(255,255,255,0.05)',
              color: filter === f.id ? '#1a1300' : 'var(--club-text-muted)',
              fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >{f.label}</button>
        ))}
      </div>

      {filtered === null ? (
        <div className="club-loading"><div className="club-loading-spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="club-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🎁</div>
          <div style={{ fontSize: 14, color: 'var(--club-text)' }}>
            {filter === 'active' ? 'Brak aktywnych kuponów. Wymień punkty na nagrody!' : 'Brak kuponów w tej kategorii'}
          </div>
        </div>
      ) : (
        <div data-testid="club-coupons-list">
          {filtered.map((c) => {
            const st = STATUS[c.status] || STATUS.expired;
            const rs = c.reward_snapshot || {};
            const active = c.status === 'active';
            return (
              <div key={c.id} className="club-card" style={{ padding: 16, marginBottom: 10 }} data-testid={`club-coupon-${c.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--club-text)' }}>
                    🎁 {rs.name || 'Kupon'}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: '4px 8px', borderRadius: 999,
                    background: `${st.color}22`, color: st.color, letterSpacing: 0.5,
                  }}>{st.icon} {st.label}</span>
                </div>
                {rs.description && (
                  <div style={{ fontSize: 12, color: 'var(--club-text-muted)', marginBottom: 12 }}>{rs.description}</div>
                )}
                <div
                  onClick={active ? () => copy(c.code) : undefined}
                  style={{
                    padding: 14, borderRadius: 10, fontFamily: 'monospace',
                    background: active ? 'rgba(212,175,55,0.10)' : 'rgba(0,0,0,0.3)',
                    border: `2px dashed ${active ? 'var(--club-gold)' : 'var(--club-border-soft)'}`,
                    textAlign: 'center', fontSize: 22, fontWeight: 900, letterSpacing: 2,
                    color: active ? 'var(--club-gold)' : 'var(--club-text-muted)',
                    cursor: active ? 'pointer' : 'default',
                  }}
                  data-testid={`club-coupon-code-${c.id}`}
                >
                  {c.code}
                </div>
                {active && (
                  <button
                    onClick={() => copy(c.code)}
                    className="club-btn club-btn-secondary"
                    style={{ marginTop: 10, fontSize: 13, padding: '10px 14px', minHeight: 0 }}
                    data-testid={`club-coupon-copy-${c.id}`}
                  >📋 Kopiuj kod</button>
                )}
                <div style={{ fontSize: 11, color: 'var(--club-text-muted)', marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Wymieniono za {c.cost_paid} pkt</span>
                  <span>{c.status === 'used' ? 'Użyto: ' : 'Ważny do: '}{fmtDate(c.used_at || c.expires_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
